/**
 * Layout lifted off the UDYOG application form (উদ্যোগ — উপজেলাভিত্তিক তরুণ উদ্যোক্তা
 * অনুসন্ধান, চিহ্নিতকরণ ও অর্থায়ন কর্মসূচি, SME & Special Programmes Department,
 * Bangladesh Bank; 7 A4 pages).
 *
 * The file is a Word export with real vector text set in Kalpurush, the same
 * face the tools use, so nothing here is solved or eyeballed: every x, baseline
 * and size is the PDF's own text matrix, and sizes carry over 1:1. The extracted
 * text itself is unusable (its ToUnicode map is scrambled), so the strings were
 * transcribed by hand from the rendered pages. Rules and shading are in
 * `udyogGeometry.js`.
 *
 * Coordinates are PDF points, y down from the top, text by baseline.
 */

export const PAGE_WIDTH = 595;
export const PAGE_HEIGHT = 842;

/** The form's own colours. */
export const COLOR = {
    ink: '#000',
    heading: '#0F3D2E',
    subheading: '#1F6F4A',
    white: '#fff',
    footer: '#777',
    shade: '#F2F2F2',
};

export const BODY_SIZE = 10;

/** Text sits in a 1.5 em line box, so descenders stay inside the clip. */
export const LINE_BOX = 1.5;

/** Where a baseline falls in that box, before the per-line snap trues it up. */
export const BASELINE_RATIO = 1.05;

/**
 * Typed answers: a touch over the 10 pt body so they stay legible on a photocopy,
 * shrinking toward `minSize` and wrapping only when the cell needs it.
 */
export const VALUE_TYPE = { size: 10.5, minSize: 7, lineHeight: 1.4 };

/**
 * Kalpurush ink runs from 0.94 em above the baseline (the ি / ী hooks) to 0.23 em
 * below it (ু, ্র). A block of answer lines is centred on that span, so it reads
 * centred between a cell's rules at any size.
 */
export const VALUE_INK = { ascent: 0.94, descent: 0.23 };

/** Left and right breathing room inside a cell. */
const PAD = 4;

const VALUE_COL = 206.93;
const PLAN_COL = 202.01;
const RIGHT = 558.7;

const text = (t, y, x, extra = {}) => ({ k: 'text', t, y, x, ...extra });
const bold = (t, y, x, extra = {}) => text(t, y, x, { bold: true, ...extra });
const band = (t, y, x, size = 11) => text(t, y, x, { size, color: COLOR.white, bold: true });

/** An answer cell: its rules' edges. Text is centred vertically, left unless `center`. */
const cell = (field, x0, y0, x1, y1, extra = {}) => ({ k: 'area', field, x: x0 + PAD, y: y0, w: x1 - x0 - PAD * 2, h: y1 - y0, ...extra });
const centered = (field, x0, y0, x1, y1, extra = {}) => cell(field, x0, y0, x1, y1, { align: 'center', ...extra });

/** A row's numeral and label lines, at the label column. */
const row = (num, numY, numX, lines) => [
    text(num, numY, numX),
    ...lines.map(([t, y]) => text(t, y, 72)),
];

/** The three money columns (মাসিক / ত্রৈমাসিক / বার্ষিক) two parts share. */
const threeUp = (prefix, columns, [hy, y0, y1]) => [
    text('মাসিক', hy, columns.labels[0]),
    text('ত্রৈমাসিক', hy, columns.labels[1]),
    text('বার্ষিক', hy, columns.labels[2]),
    centered(`${prefix}Monthly`, columns.x[0], y0, columns.x[1], y1),
    centered(`${prefix}Quarterly`, columns.x[1], y0, columns.x[2], y1),
    centered(`${prefix}Yearly`, columns.x[2], y0, columns.x[3], y1),
];

const addressHead = (first) => [
    bold('বাড়ি/মহল্লার নং', first, 50),
    bold('রোড নং/ এলাকা/', first, 137.2),
    bold('ও নাম', first + 17, 66.7),
    bold('গ্রামের নাম', first + 17, 150.7),
];

const ADDRESS_COLS = [36, 119.66, 219.77, 297.41, 386.35, 470.02, 558.94];
const ADDRESS_PARTS = ['House', 'Road', 'PostOffice', 'Thana', 'District', 'Division'];

const addressCells = (prefix, y0, y1) => ADDRESS_PARTS.map((part, i) => (
    centered(`${prefix}${part}`, ADDRESS_COLS[i], y0, ADDRESS_COLS[i + 1], y1)
));

/** Every page's foot: small logo, programme name, page number. */
const footer = (page) => [
    { k: 'logo', x: 128.29, y: 790.9, w: 11.68, h: 12 },
    text('উদ্যোগ (UDYOG): উপজেলাভিত্তিক তরুণ উদ্যোক্তা অনুসন্ধান, চিহ্নিতকরণ ও অর্থায়ন কর্মসূচি', 803, 142.5, { size: 9, color: COLOR.footer }),
    text('|', 803, 442.3, { size: 9, color: COLOR.footer }),
    text('পৃষ্ঠা', 803, 447.6, { size: 9, color: COLOR.footer }),
    text(String(page), 803, 462.8, { size: 9, color: COLOR.footer }),
];

const PAGE1 = [
    { k: 'logo', x: 36, y: 27.7, w: 59.2, h: 64.77 },
    bold('উদ্যোগ (UDYOG)', 48.8, 104.2, { size: 18, color: COLOR.heading }),
    bold('উপজেলাভিত্তিক তরুণ উদ্যোক্তা অনুসন্ধান, চিহ্নিতকরণ ও অর্থায়ন কর্মসূচি', 70.2, 104.2, { size: 12, color: COLOR.subheading }),
    text('এসএমই এন্ড স্পেশাল প্রোগ্রামস্ ডিপার্টমেন্ট, বাংলাদেশ ব্যাংক।', 88, 104.2, { size: 11 }),
    bold('আবেদন ফরম', 124, 263.4, { size: 14 }),
    text('(আবেদন জমাদানের সর্বশেষ তারিখ ৩০ নভেম্বর ২০২৬)', 141.4, 198.7),

    // ব্যাংক শাখায় ব্যবহারের জন্য — filled at the branch, printed blank.
    text('ব্যাংক শাখায় ব্যবহারের জন্য', 172.6, 185.6, { color: COLOR.white, bold: true }),
    text('পাসপোর্ট সাইজ ছবি (২ কপি)', 172.2, 438.9, { size: 11, color: COLOR.white, bold: true }),
    text('ট্র্যাকিং নম্বর:', 192.5, 41.6),
    text('আবেদন গ্রহণকারী ব্যাংক ও শাখা:', 226.8, 41.6),
    text('গ্রহণের তারিখ:', 261.1, 41.6),
    text('২', 261.9, 332.5, { size: 12 }),
    text('০', 261.9, 360.2, { size: 12 }),
    text('২', 261.9, 388.4, { size: 12 }),
    text('৬', 261.9, 416, { size: 12 }),
    text('জেলা:', 284, 41.6),
    text('উপজেলা:', 306.8, 41.6),
    text('শাখা ব্যবস্থাপকের নাম:', 329.7, 41.6),
    text('স্বাক্ষর ও সীল:', 374.8, 41.6),

    band('পার্ট-১: ব্যক্তিগত তথ্যাবলি', 430.5, 248.9),
    ...row('০১', 483.9, 46.9, [['আবেদনকারীর নাম', 483.9]]),
    text('বাংলায়', 464.9, 210.2),
    text('In English', 495.7, 210.2),
    text('(Block Letters)', 511.4, 210.2),
    cell('nameBn', 284.21, 440.95, RIGHT, 480.43),
    cell('nameEnUpper', 284.21, 480.43, RIGHT, 518.35),
    ...row('০২', 533.6, 46.4, [['পিতার নাম', 533.6]]),
    cell('fatherName', VALUE_COL, 518.35, RIGHT, 540.67),
    ...row('০৩', 555.9, 46.2, [['মাতার নাম', 555.9]]),
    cell('motherName', VALUE_COL, 540.67, RIGHT, 562.87),
    ...row('০৪', 578.1, 46.6, [['জাতীয় পরিচয়পত্র নম্বর', 578.1]]),
    cell('nid', VALUE_COL, 562.87, RIGHT, 585.07),
    ...row('০৫', 600.4, 46.3, [['জন্ম তারিখ ও আবেদনের শেষ', 600.4], ['তারিখে বয়স', 616.1]]),
    cell('birthLine', VALUE_COL, 585.07, RIGHT, 623.14),
    ...row('০৬', 638.4, 46.2, [['বৈবাহিক অবস্থা', 638.4]]),
    cell('maritalLabel', VALUE_COL, 623.14, RIGHT, 645.46),
    ...row('০৭', 679.7, 46.4, [['(বিবাহিত হলে)', 660.7], ['স্বামী/স্ত্রীর নাম:', 676.4], ['জাতীয় পরিচয়পত্র নম্বর', 698.6]]),
    cell('spouseName', VALUE_COL, 645.46, RIGHT, 683.38),
    cell('spouseNid', VALUE_COL, 683.38, RIGHT, 705.7),
    ...row('০৮', 720.9, 46.1, [['শিক্ষাগত যোগ্যতা (সর্বশেষ পাসকৃত', 720.9], ['পরীক্ষা, বোর্ড/বিশ্ববিদ্যালয় ও সাল)', 736.7]]),
    cell('education', VALUE_COL, 705.7, RIGHT, 743.62),
    ...row('০৯', 758.9, 46.3, [['ফোন নম্বর / বিকল্প ফোন নম্বর', 758.9]]),
    cell('phoneLine', VALUE_COL, 743.62, RIGHT, 765.94),
    ...row('১০', 781.2, 46.9, [['ই-মেইল আইডি', 781.2]]),
    cell('email', VALUE_COL, 765.94, RIGHT, 788.14),
];

const PAGE2 = [
    ...row('১১', 66.8, 47.5, [
        ['দক্ষতা উন্নয়নমূলক কোনরূপ প্রশিক্ষণ', 43.2],
        ['থাকলে তার বিবরণ (প্রতিষ্ঠান,', 58.9],
        ['কোর্সের নাম, মেয়াদ ও সাল; সনদের', 74.8],
        ['কপি সংযুক্ত করুন)', 90.5],
    ]),
    cell('training', VALUE_COL, 27.72, RIGHT, 97.46),
    ...row('১২', 161.8, 47, [['ব্যবসা/চাকরির অভিজ্ঞতা থাকলে তার', 154], ['বিবরণ', 169.7]]),
    cell('experience', VALUE_COL, 97.46, RIGHT, 217.94),
    text('(ব্যাংকের নাম ও শাখা, হিসাবের ধরণ, হিসাব নম্বর)', 233.2, 293.4),
    ...row('১৩', 280.4, 46.8, [['ব্যাংক হিসাব থাকলে ব্যাংক হিসাবের', 272.4], ['তথ্য', 288.3]]),
    cell('bankAccount', VALUE_COL, 240.14, RIGHT, 334.61),
    ...row('১৪', 388.4, 47.2, [
        ['কোনো ব্যাংক/আর্থিক প্রতিষ্ঠানে', 372.5],
        ['বিদ্যমান ঋণ আছে কিনা', 388.4],
        ['(হ্যাঁ/না); থাকলে বিবরণ', 404.1],
    ]),
    cell('existingLoan', VALUE_COL, 334.61, RIGHT, 433.85),

    bold('১৫. বর্তমান ঠিকানা (জাতীয় পরিচয়পত্র মোতাবেক)', 454.4, 36),
    ...addressHead(475.7),
    bold('ডাকঘর ও', 475.7, 241.1),
    bold('পোস্ট কোড', 492.5, 237.9),
    bold('থানা/উপজেলা', 484.1, 317.1),
    bold('জেলা', 484.1, 419.4),
    bold('বিভাগ', 484.1, 504.2),
    ...addressCells('present', 497.35, 565.75),

    bold('১৬. স্থায়ী ঠিকানা', 586, 36),
    ...addressHead(607.4),
    bold('ডাকঘর ও পোস্ট', 607.8, 229.3),
    bold('কোড', 623.6, 249.8),
    bold('থানা/উপজেলা', 615.8, 317.1),
    bold('জেলা', 615.8, 419.4),
    bold('বিভাগ', 615.8, 504.2),
    ...addressCells('permanent', 629.02, 696.58),
];

const PLAN_MONEY = { x: [PLAN_COL, 313.15, 432.91, RIGHT], labels: [246.7, 357.6, 485.4] };

const PAGE3 = [
    band('পার্ট-২: ব্যবসায়িক পরিকল্পনা সংক্রান্ত তথ্যাদি (ইতোপূর্বে ব্যবসা না থেকে থাকলে)', 71, 142.5),
    ...row('০১', 235.5, 46.9, [['ব্যবসায়িক ধারণা/উদ্যোগের সংক্ষিপ্ত', 219.6], ['বিবরণ', 235.5], ['(২০০ শব্দের মধ্যে)', 251.2]]),
    cell('businessIdea', PLAN_COL, 81.36, RIGHT, 380.93),
    ...row('০২', 396.2, 46.4, [['সম্ভাব্য ক্রেতা', 396.2]]),
    cell('buyers', PLAN_COL, 380.93, RIGHT, 403.25),
    ...row('০৩', 426.3, 46.2, [['কত সময়ের মধ্যে ব্যবসায় শুরু', 418.5], ['করার পরিকল্পনা রয়েছে', 434.2]]),
    cell('startTimeline', PLAN_COL, 403.25, RIGHT, 441.19),
    ...row('০৪', 478.1, 46.6, [['ব্যবসার ধরন', 462.4], ['(উৎপাদন / সেবা / ব্যবসা)', 478.1], ['ও বিবরণ', 493.9]]),
    cell('businessType', PLAN_COL, 441.19, RIGHT, 506.71),
    ...row('০৫', 542.6, 46.3, [['ব্যবসার সম্ভাব্য অবস্থান (গ্রাম/মহল্লা,', 522.1], ['ইউনিয়ন, উপজেলা)', 537.8]]),
    cell('businessLocation', PLAN_COL, 506.71, RIGHT, 570.07),
    ...row('০৬', 601, 46.2, [['পণ্যের ধরন', 585.3], ['(আমদানির বিকল্প বা রপ্তানিযোগ্য', 601], ['পণ্য কিনা)', 616.9]]),
    cell('productType', PLAN_COL, 570.07, RIGHT, 623.86),
    ...row('০৭', 647, 46.4, [['কাঁচামালের উৎস', 639.1], ['(প্রযোজ্য ক্ষেত্রে)', 654.8]]),
    cell('rawMaterial', PLAN_COL, 623.86, RIGHT, 661.9),
    ...row('০৮', 684.9, 46.1, [['পণ্যের বাজার', 677.1], ['(স্থানীয়/আন্তর্জাতিক)', 692.9]]),
    cell('productMarket', PLAN_COL, 661.9, RIGHT, 699.82),
    ...row('০৯', 726.2, 46.3, [['সম্ভাব্য বিক্রয়ের পরিমাণ', 718.4], ['(টাকায়)', 734.1]]),
    ...threeUp('sales', PLAN_MONEY, [715.1, 722.14, 744.34]),
    ...row('১০', 770.7, 46.9, [['সম্ভাব্য লাভ/মুনাফা', 762.8], ['(টাকায়)', 778.7]]),
    ...threeUp('profit', PLAN_MONEY, [759.6, 766.66, 788.86]),
];

const BUSINESS_MONEY = { x: [PLAN_COL, 306.29, 429.43, RIGHT], labels: [243.2, 352.4, 483.7] };

const PAGE4 = [
    ...row('১১', 80.6, 47.5, [
        ['ব্যবসা পরিচালনার ক্ষেত্রে সম্ভাব্য', 64.8],
        ['সমস্যাসমূহ, ঝুঁকিসমূহ এবং', 80.6],
        ['ঝুকিসমূহ মোকাবেলায় পরিকল্পনা', 96.4],
    ]),
    cell('risks', PLAN_COL, 27.72, RIGHT, 125.06),
    ...row('১২', 140.3, 47, [['প্রথম বছরে সম্ভাব্য কর্মসংস্থান', 140.3], ['(মোট ......... জন,', 156], ['তন্মধ্যে নারী ......... জন)', 171.7]]),
    { k: 'dots', field: 'jobsTotal', y: 156, x: 95.5, w: 24 },
    { k: 'dots', field: 'jobsFemale', y: 171.7, x: 124.5, w: 24 },
    cell('jobsNote', PLAN_COL, 125.06, RIGHT, 178.82),
    ...row('১৩', 252.9, 46.8, [
        ['যে পণ্য/সেবা বিক্রয় করা হবে,', 221.3],
        ['বর্তমানে বাজারে তার অবস্থান', 237],
        ['কেমন', 252.9],
        ['(কমপক্ষে ৩টি সম্ভাব্য প্রতিযোগী', 268.6],
        ['প্রতিষ্ঠানের তথ্যসহ)', 284.3],
    ]),
    cell('marketPosition', PLAN_COL, 178.82, RIGHT, 318.65),

    band('পার্ট-৩: ব্যবসা সম্পর্কিত তথ্যাবলি (বর্তমানে কোন ব্যবসা পরিচালনা করে থাকলে)', 345.3, 142.7),
    ...row('০১', 371.2, 46.9, [['ব্যবসা প্রতিষ্ঠানের নাম', 371.2]]),
    cell('bizName', PLAN_COL, 355.73, RIGHT, 378.17),
    ...row('০২', 409.1, 46.4, [['ব্যবসার ধরন', 393.4], ['(উৎপাদন / সেবা / ব্যবসা)', 409.1], ['ও বিবরণ', 425]]),
    cell('bizType', PLAN_COL, 378.17, RIGHT, 431.93),
    ...row('০৩', 447.2, 46.2, [['ব্যবসা শুরুর তারিখ', 447.2]]),
    cell('bizStartDate', PLAN_COL, 431.93, RIGHT, 454.15),
    ...row('০৪', 485.2, 46.6, [['ট্রেড লাইসেন্স আছে কিনা', 469.5], ['(হ্যাঁ/না) এবং থাকলে', 485.2], ['ইস্যুর তারিখ ও নম্বর', 500.9]]),
    cell('tradeLicense', PLAN_COL, 454.15, RIGHT, 507.91),
    ...row('০৫', 548, 46.3, [['পণ্য/সেবার গড় বিক্রয়মূল্য (একক', 540.2], ['প্রতি)', 555.9]]),
    cell('avgPrice', PLAN_COL, 507.91, RIGHT, 579.79),
    ...row('০৬', 606.2, 46.2, [['বিক্রয়ের পরিমাণ', 598.3], ['(টাকায়)', 614.1]]),
    ...threeUp('bizSales', BUSINESS_MONEY, [595, 601.99, 624.34]),
    ...row('০৭', 650.7, 46.4, [['লাভ/মুনাফার পরিমাণ', 642.8], ['(টাকায়)', 658.5]]),
    ...threeUp('bizProfit', BUSINESS_MONEY, [639.6, 646.54, 668.86]),
    ...row('০৮', 715.8, 46.1, [['কাঁচামাল ও ব্যবসা পরিচালনা বাবদ', 700.1], ['বিভিন্ন খাতে মাসিক সম্ভাব্য খরচ', 715.8], ['(টাকা)', 731.5]]),
    cell('monthlyCost', PLAN_COL, 668.86, RIGHT, 754.54),
];

const PAGE5 = [
    ...row('০৯', 76.1, 46.3, [['উদ্যোক্তা কীভাবে আলোচ্য ব্যবসায়ের', 68.3], ['সঙ্গে জড়িত হয়েছেন', 84]]),
    cell('involvement', PLAN_COL, 27.72, RIGHT, 116.06),
    ...row('১০', 139.1, 46.9, [['ব্যবসার সঙ্গে সম্পর্কিত মোট', 131.3], ['বিনিয়োগের পরিমাণ (টাকা)', 147]]),
    cell('totalInvestment', PLAN_COL, 116.06, RIGHT, 153.98),
    ...row('১১', 184, 47.5, [['কাঁচামালের উৎস', 176.1], ['(প্রযোজ্য ক্ষেত্রে)', 191.8]]),
    cell('bizRawMaterial', PLAN_COL, 153.98, RIGHT, 205.58),
    ...row('১২', 220.9, 47, [['পণ্য/সেবা বিপণন এলাকা', 220.9]]),
    cell('marketingArea', PLAN_COL, 205.58, RIGHT, 227.9),
    ...row('১৩', 302, 46.8, [
        ['যে পণ্য/সেবা বিক্রয় করা হয়,', 278.3],
        ['বর্তমানে বাজারে তার অবস্থান', 294],
        ['(কমপক্ষে ৩টি সম্ভাব্য প্রতিযোগী', 309.8],
        ['প্রতিষ্ঠানের তথ্যসহ)', 325.6],
    ]),
    cell('bizMarketPosition', PLAN_COL, 227.9, RIGHT, 367.73),
    ...row('১৪', 439.5, 47.2, [
        ['বিদ্যমান ব্যবসায় পরিচালনায়', 423.8],
        ['সমস্যাসমূহ, ঝুঁকিসমূহ এবং', 439.5],
        ['ঝুকিসমূহ মোকাবেলায় পরিকল্পনা', 455.2],
    ]),
    cell('bizRisks', PLAN_COL, 367.73, RIGHT, 502.99),
    ...row('১৫', 534.1, 46.9, [['কর্মীর সংখ্যা', 518.2], ['(মোট ......... জন,', 534.1], ['তন্মধ্যে নারী ......... জন)', 549.8]]),
    { k: 'dots', field: 'staffTotal', y: 534.1, x: 95.5, w: 24 },
    { k: 'dots', field: 'staffFemale', y: 549.8, x: 124.5, w: 24 },
    cell('staffNote', PLAN_COL, 502.99, RIGHT, 556.75),
    ...row('১৬', 662.1, 46.8, [
        ['চলমান ব্যবসায় সম্প্রসারণসহ কর্মী', 646.4],
        ['বাড়ানোর কোনো পরিকল্পনা রয়েছে', 662.1],
        ['কিনা; থাকলে বিবরণ', 677.9],
    ]),
    cell('expansionPlan', PLAN_COL, 556.75, RIGHT, 759.22),
];

/** খ — the twelve spending heads, in the form's order. */
export const COST_HEADS = [
    'যন্ত্রপাতি ও সরঞ্জাম ক্রয়',
    'কাঁচামাল ও উপকরণ',
    'পরিচলন ব্যয়',
    'প্রোটোটাইপ/নমুনা উন্নয়ন',
    'প্যাকেজিং, লেবেল ও ব্র্যান্ডিং',
    'ব্যবসা নিবন্ধন, লাইসেন্স ও ছাড়পত্র',
    'বিপণন ও প্রচার',
    'কর্মীর মজুরি',
    'পরিবহন ও সরবরাহ',
    'প্রযুক্তি, সফটওয়্যার ও ইন্টারনেট',
    'প্রশিক্ষণ ও দক্ষতা উন্নয়ন',
    'অন্যান্য (উল্লেখ করুন)',
];

const COST_NUMS = ['০১', '০২', '০৩', '০৪', '০৫', '০৬', '০৭', '০৮', '০৯', '১০', '১১', '১২'];
const COST_NUM_X = [55.1, 54.6, 54.4, 54.7, 54.5, 54.4, 54.6, 54.2, 54.5, 55.1, 55.7, 55.2];
const COST_RULES = [218.06, 235.34, 252.5, 269.81, 287.09, 304.37, 321.53, 338.81, 356.09, 373.37, 390.53, 407.81, 425.09, 442.39];
const COST_BASELINES = [230.4, 247.7, 265, 282.2, 299.4, 316.7, 334, 351.2, 368.4, 385.7, 403, 420.2];

const costRow = (i) => {
    const y0 = COST_RULES[i];
    const y1 = COST_RULES[i + 1];
    return [
        text(COST_NUMS[i], COST_BASELINES[i], COST_NUM_X[i]),
        text(COST_HEADS[i], COST_BASELINES[i], 89.4),
        cell(`cost${i + 1}Detail`, 234.05, y0, 362.11, y1),
        centered(`cost${i + 1}Amount`, 362.11, y0, 470.86, y1),
        centered(`cost${i + 1}Source`, 470.86, y0, 558.94, y1),
    ];
};

const PAGE6 = [
    band('পার্ট-৪: অর্থায়ন পরিকল্পনা ও ব্যয় বিভাজন', 69.5, 217),
    bold('ক. অর্থায়ন প্যাকেজ', 93.6, 36),
    bold('বিবরণ', 115, 160.8),
    bold('পরিমাণ (টাকা)', 115, 407.7),
    text('০১. প্রয়োজনীয় মোট মূলধনের পরিমাণ', 132.1, 41.6),
    centered('capitalRequired', 307.97, 119.78, 558.94, 137.06),
    text('০২. নিজস্ব বিনিয়োগ (যদি থাকে)', 149.4, 41.6),
    centered('ownInvestment', 307.97, 137.06, 558.94, 154.34),
    text('০৩. আবেদনকৃত অর্থায়নের পরিমাণ', 166.7, 41.6),
    centered('financingRequested', 307.97, 154.34, 558.94, 171.62),

    bold('খ. খাতভিত্তিক ব্যয় বিভাজন (প্রযোজ্য ঘরসমূহ পূরণীয়)', 191.9, 36),
    bold('ক্রম', 213.1, 53.4),
    bold('ব্যয়ের খাত', 213.1, 139.8),
    bold('বিবরণ', 213.1, 287),
    bold('মোট (টাকা)', 213.1, 395.9),
    bold('অর্থের উৎস', 213.1, 494.6),
    ...COST_HEADS.flatMap((_, i) => costRow(i)),
    bold('সর্বমোট', 437.4, 121.8),
    centered('costTotal', 362.11, 425.09, 470.86, 442.39),
];

const PAGE7 = [
    band('পার্ট-৫: অন্যান্য তথ্য', 69.4, 259.5),
    text('০১', 95.3, 52.2),
    text('শারীরিক প্রতিবন্ধকতা আছে কিনা (হ্যাঁ / না); থাকলে ধরন', 95.3, 82.7),
    cell('disability', 318.43, 79.8, RIGHT, 102.26),
    text('০২', 117.5, 51.7),
    text('ক্ষুদ্র নৃগোষ্ঠীভুক্ত কিনা (হ্যাঁ / না)', 117.5, 82.7),
    cell('ethnicMinority', 318.43, 102.26, RIGHT, 124.58),
    text('০৩', 139.8, 51.5),
    text('অন্যান্য', 139.8, 82.7),
    cell('otherInfo', 318.43, 124.58, RIGHT, 146.78),

    bold('ঘোষণা', 191.1, 39),
    text('আমি এই মর্মে ঘোষণা করছি যে, উপরে প্রদত্ত সমস্ত তথ্য সত্য ও সঠিক। কোনো তথ্য অসত্য প্রমাণিত হলে আমার আবেদন/অনুমোদন যেকোনো', 211.8, 41.4),
    text('পর্যায়ে বাতিলযোগ্য হবে।', 227.7, 41.4),
    text('আমি অবগত আছি যে অনুমোদিত হলে, প্রাপ্ত ঋণ ও অনুদান নির্ধারিত খাতে ব্যবহৃত হলে এবং ঋণ অংশ সম্পূর্ণ ও যথাসময়ে পরিশোধ করলেই কেবল', 244.3, 41.4, { right: 554.3 }),
    text('অনুদানের প্রাপ্ত অর্থ অনুদান হিসেবে বিবেচিত হবে এবং ঋণের সদ্ব্যবহার না করা হলে হলে অনুদান অংশ ঋণে পরিণত হবে এবং সম্পূর্ণ অর্থ ঋণ', 260.1, 41.4, { right: 554.3 }),
    text('হিসেবে গণ্য হয়ে আমি খেলাপি হিসেবে চিহ্নিত হব। আমি অঙ্গীকার করছি যে প্রদানকৃত অর্থ ব্যক্তিগত ভোগ, জমি বা যানবাহন ক্রয়ে ব্যবহার করব', 275.8, 41.4, { right: 554.3 }),
    text('না; প্রতিটি ব্যয়ের ভাউচার সংরক্ষণ করব এবং চাহিদামতো প্রতিবেদন দাখিল করব।', 291.6, 41.4),

    text(`আবেদনকারীর স্বাক্ষর ও তারিখ: ${'.'.repeat(64)}`, 358.7, 41.4),
    { k: 'dots', field: 'applicationDate', y: 358.7, x: 216, w: 86 },

    bold('সংযুক্তি (আবেদনের সঙ্গে দাখিলযোগ্য)', 408.8, 36),
    ...[
        ['জাতীয় পরিচয়পত্র/জন্মনিবন্ধন সনদের কপি;', 429.5],
        ['সর্বশেষ শিক্ষাগত যোগ্যতার সনদের কপি; (যদি থাকে)', 448.3],
        ['দক্ষতা উন্নয়ন/উদ্যোক্তা উন্নয়ন প্রশিক্ষণ সনদ (যদি থাকে);', 467.1],
        ['ওয়ার্ড কমিশনার/কাউন্সিলর কর্তৃক প্রদত্ত নাগরিক সনদ।', 485.8],
    ].flatMap(([t, y]) => [{ k: 'bullet', x: 46.5, y: y - 3.4 }, text(t, y, 56)]),
];

export const PAGES = [PAGE1, PAGE2, PAGE3, PAGE4, PAGE5, PAGE6, PAGE7]
    .map((items, i) => [...items, ...footer(i + 1)]);
